import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_GG_PUBLISHABLE_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// 創建 Supabase 客戶端,明確禁用 Realtime 功能以提升效能
export const supabase = createClient(supabaseUrl, supabaseKey, {
  realtime: {
    // 專案未使用 Realtime 功能,禁用以減少資源消耗和避免 Preview 環境的連接問題
    params: {
      eventsPerSecond: 0
    }
  },
  auth: {
    autoRefreshToken: true,
    persistSession: true
  }
});

export interface UserProfile {
  id: string;
  world_id_sub: string;
  username?: string;
  is_new_user: boolean;
  created_at: string;
  updated_at: string;
  wallet_address?: string;
  world_id_credential_type?: string;
  is_orb_verified?: boolean;
  last_auth_method?: string;
  email?: string;
  oauth_provider?: string;
  gmail_merged?: boolean;
  gmail_address?: string;
}

// 用戶相關的 API 函數
export const userAPI = {
  // 獲取當前登入用戶的個人資料
  async getProfile(): Promise<UserProfile | null> {
    // 獲取當前登入的用戶
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ [getProfile] Error getting current user:', userError);
      return null;
    }
    
    if (!user) {
      console.warn('⚠️ [getProfile] No authenticated user found');
      return null;
    }
    
    // 從 profiles 表獲取用戶資料
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    
    if (error) {
      console.error('❌ [getProfile] Error fetching user profile:', error);
      return null;
    }
    
    if (data) {
      // 驗證返回的資料是否完整 (現在 world_id_sub 不是必需的)
      if (!data.id) {
        console.error('❌ Incomplete user data - missing ID:', data);
        throw new Error('用戶資料不完整 - 缺少 ID');
      }
      
      // 記錄用戶識別方式
      if (data.wallet_address) {
        console.log('👤 [getProfile] User identified by wallet_address:', data.wallet_address.slice(0, 6) + '...' + data.wallet_address.slice(-4));
      } else if (data.world_id_sub) {
        console.log('👤 [getProfile] User identified by world_id_sub:', data.world_id_sub.slice(0, 8) + '...');
      } else {
        console.warn('⚠️ [getProfile] User has no primary identifier (wallet_address or world_id_sub)');
      }
    }
    
    return data;
  },

  // 更新當前用戶資料
  async update(updates: Partial<Pick<UserProfile, 'is_new_user'>>): Promise<UserProfile> {
    console.log('🔄 Updating current user profile:', updates);
    
    // 獲取當前登入的用戶
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ Error getting current user:', userError);
      throw userError;
    }
    
    if (!user) {
      throw new Error('未找到登入用戶');
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)
      .select()
      .single();
    
    if (error) {
      console.error('❌ Error updating user profile:', error);
      console.error('❌ Error details:', error.details);
      console.error('❌ Error hint:', error.hint);
      console.error('❌ Error code:', error.code);
      throw error;
    }
    
    if (!data) {
      throw new Error('更新操作沒有返回資料');
    }
    
    console.log('✅ User profile updated:', data);
    return data;
  },

  // 刪除當前用戶記錄
  async delete(): Promise<void> {
    console.log('🗑️ Deleting current user profile...');
    
    // 獲取當前登入的用戶
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('❌ Error getting current user:', userError);
      throw userError;
    }
    
    if (!user) {
      throw new Error('未找到登入用戶');
    }
    
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', user.id);
    
    if (error) {
      console.error('❌ Error deleting user profile:', error);
      console.error('❌ Error details:', error.details);
      console.error('❌ Error hint:', error.hint);
      console.error('❌ Error code:', error.code);
      throw error;
    }
    
    console.log('✅ User profile deleted successfully');
  },

  // 標記用戶為已完成新手引導
  async markAsExistingUser(): Promise<UserProfile> {
    console.log('🎯 Marking user as existing user...');
    return await this.update({ is_new_user: false });
  }
};